import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import errorToast from "../../../../helper-components/error-toast";

import LoadingSpinner from "../../../../helper-components/loading-spinner";
import { Field, Form, Formik } from "formik";

import AddButton from "../../../../buttons/button";
import DeleteIcon from "../../../../../assets/icons/delete-icon";

import successToast from "../../../../helper-components/success-toast";
import {
    serverSearchListAction,
    serverCreateAction,
    serverDeleteAction,
    serverEditAction,
} from "../../../../../redux/actions/settings-action";
import SelectField from "../../../../forms/select-field";
import { mapListToOptions } from "../helper/helper-comp";
import SearchCustomerField from "../../../../forms/search-customer-field";
import { phoneListAction } from "../../../../../redux/actions/general-action";

function ServerComp({ pageLoading, setPageLoading, searchValue, setReload, reload }) {
    const [initialData, setItemTypeData] = useState([]);

    const [loading, setLoading] = useState(false);
    const [debouncedSearchValue, setDebouncedSearchValue] = useState("");

    const tableHeading = [
        "Server IP Address",
        "Server Name",
        "Customer Name",
        "Actions",
    ];

    const initialValues = useMemo(() => {
        return {
            data: initialData?.map((item) => ({
                ...item,
                customer_selection: item.cmr_id
                    ? {
                        label: item.cmr_first_name,
                        value: item.cmr_id,
                    }
                    : null,
            })) || [],
            newLine: {
                ip_address: "",
                server_name: "",
                customer_selection: null,
            },
        };
    }, [initialData]);

    const dispatch = useDispatch();

    const onSuccess = useCallback((data) => {
        setItemTypeData(data?.data?.serverIps || []);
        setLoading(false);
        setPageLoading(false);
    }, [setPageLoading]);

    const onError = useCallback((data) => {
        setLoading(false);
        setPageLoading(false);
        errorToast(data?.data?.message || "Something went wrong");
    }, [setPageLoading]);

    const onSaveSuccess = useCallback((data) => {
        setLoading(false);
        setPageLoading(false);
        successToast(data?.message || "Action successful");
        fetchData(debouncedSearchValue);
    }, [debouncedSearchValue, setPageLoading]);

    const onSaveError = useCallback((data) => {
        setLoading(false);
        setPageLoading(false);
        errorToast(data?.data?.message || "Action failed");
    }, [setPageLoading]);

    const fetchData = useCallback((search = "") => {
        setLoading(true);
        setPageLoading(true);
        dispatch(serverSearchListAction(search, onSuccess, onError));
    }, [dispatch, onSuccess, onError, setPageLoading]);

    // Consolidated effect for initial load and search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
        }, 800);

        return () => clearTimeout(timer);
    }, [searchValue]);

    useEffect(() => {
        fetchData(debouncedSearchValue);
    }, [debouncedSearchValue, fetchData]);

    useEffect(() => {
        if (reload) {
            fetchData(debouncedSearchValue);
            setReload(false);
        }
    }, [reload, debouncedSearchValue, fetchData, setReload]);

    const onSubmit = (values) => {
        if (!loading) {
            initialData?.forEach((item, index) => {
                const currentItem = values.data[index];
                const cmr_id = currentItem.customer_selection?.value || "";

                // Construct payload exactly as requested (Single object, not array)
                const updatePayload = {
                    server_id: currentItem.server_id,
                    server_name: currentItem.server_name,
                    ip_address: currentItem.ip_address,
                    cmr_id: cmr_id,
                };

                // Compare relevant fields for changes
                const hasChanged =
                    updatePayload.server_name !== item.server_name ||
                    updatePayload.ip_address !== item.ip_address ||
                    updatePayload.cmr_id !== (item.cmr_id || "");

                if (hasChanged) {
                    setLoading(true);
                    setPageLoading(true);
                    dispatch(serverEditAction(updatePayload, onSaveSuccess, onSaveError));
                }
            });

            if (
                values.newLine.ip_address !== "" ||
                values.newLine.server_name !== "" ||
                values.newLine.customer_selection !== null
            ) {
                if (
                    values.newLine.ip_address === "" ||
                    values.newLine.server_name === "" ||
                    values.newLine.customer_selection === null
                ) {
                    errorToast("IP Address, Server Name and Customer Name are required");
                } else {
                    const newLineObject = {
                        server_name: values.newLine.server_name,
                        ip_address: values.newLine.ip_address,
                        cmr_id: values.newLine.customer_selection?.value || "",
                    };

                    setLoading(true);
                    setPageLoading(true);
                    dispatch(serverCreateAction(newLineObject, onSaveSuccess, onSaveError));
                }
            }
        }
    };

    const onClickDelete = (serverId) => {
        if (!loading) {
            setLoading(true);
            setPageLoading(true);
            dispatch(serverDeleteAction({ server_id: serverId }, onSaveSuccess, onSaveError));
        }
    };

    const loadOptions = useCallback((inputValue) => {
        return new Promise((resolve) => {
            const onSuccess = (data) => {
                const options = data.data.map((item) => ({
                    value: item.cmr_id,
                    label: item.cmr_first_name
                        ? item.cmr_first_name + (item.cmr_phone_number ? `, ${item.cmr_phone_number}` : "")
                        : item.cmr_phone_number,
                }));
                resolve(options);
            };

            const onError = (error) => {
                resolve([]);
            };

            if (inputValue?.length > 0) {
                dispatch(phoneListAction({ phone: inputValue }, onSuccess, onError));
            } else {
                resolve([]);
            }
        });
    }, [dispatch]);


    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={onSubmit}
        >
            {({ values, setFieldValue, handleSubmit }) => (
                <Form className="pt-1">
                    <div
                        className="flex flex-col justify-between rounded-xl"
                        style={{ height: "74vh" }}
                    >
                        {loading ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="overflow-auto mb-auto rounded-xl table-container border border-gray-200">
                                <table className=" w-full ">
                                    <thead className="sticky top-0 bg-[var(--create-table-header-bg)] z-[10]">
                                        <tr className="relative h-[50px]">
                                            {tableHeading.map((heading, index) => (
                                                <th
                                                    className={`text-start px-[12px] whitespace-nowrap text-[var(--create-table-header-fg)] text-[length:var(--create-table-header-fs)] font-[var(--create-table-header-fw)] ${index === 0 ? "w-[25%]" :
                                                        index === 1 ? "w-[30%]" :
                                                            index === 2 ? "w-[30%]" : "w-[15%]"
                                                        }`}
                                                    key={heading}
                                                >
                                                    {heading?.toUpperCase()}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {values?.data?.map((item, index) => (
                                            <tr
                                                className={` bg-[var(--list-table-row-bg)]
                         h-[50px] `}
                                                key={index}
                                            >
                                                <td className="tab-cell text-sm fw_400 text-secondary w-[25%] px-3">
                                                    <Field
                                                        name={`data[${index}].ip_address`}
                                                        placeholder="Enter IP Address"
                                                        className="w-full border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2"
                                                    />
                                                </td>
                                                <td className="tab-cell text-sm fw_400 text-secondary w-[30%] px-3">
                                                    <Field
                                                        name={`data[${index}].server_name`}
                                                        placeholder="Enter Server Name"
                                                        className="w-full border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded py-2"
                                                    />
                                                </td>
                                                <td className="tab-cell text-sm fw_400 text-secondary w-[30%] px-3">
                                                    <SearchCustomerField
                                                        className="z-20"
                                                        isValidNewOption={false}
                                                        name={`data[${index}].customer_selection`}
                                                        placeholder="Select Customer Name"
                                                        isLabelReq={false}
                                                        loadOptions={loadOptions}
                                                        disabled={loading}
                                                        menuPortalTarget={document.body}
                                                        onChange={(selectedOption) => {
                                                            setFieldValue(`data[${index}].customer_selection`, selectedOption);
                                                        }}
                                                    />
                                                </td>

                                                <td className="tab-cell align-middle text-sm fw_400 text-secondary w-[15%] px-3">
                                                    <div className="flex items-center">
                                                        <button
                                                            type="button"
                                                            className="cursor-pointer"
                                                            onClick={() => onClickDelete(item?.server_id)}
                                                        >
                                                            <DeleteIcon width="20" height="20" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        <tr>
                                            <td className="tab-cell text-sm fw_400 text-secondary w-[25%] px-3">
                                                <Field
                                                    name="newLine.ip_address"
                                                    placeholder="Enter IP Address"
                                                    className="w-full border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2"
                                                />
                                            </td>
                                            <td className="tab-cell text-sm fw_400 text-secondary w-[30%] px-3">
                                                <Field
                                                    name="newLine.server_name"
                                                    placeholder="Enter Server Name"
                                                    className="w-full border-0 focus:outline-none focus:ring focus:ring-blue-500 rounded px-1 py-2"
                                                />
                                            </td>
                                            <td className="tab-cell text-sm fw_400 text-secondary w-[30%] px-3">
                                                <SearchCustomerField
                                                    className="z-20"
                                                    isValidNewOption={false}
                                                    name="newLine.customer_selection"
                                                    placeholder="Select Customer Name"
                                                    isLabelReq={false}
                                                    loadOptions={loadOptions}
                                                    disabled={loading}
                                                    menuPortalTarget={document.body}
                                                    onChange={(selectedOption) => {
                                                        setFieldValue(`newLine.customer_selection`, selectedOption);
                                                    }}
                                                />
                                            </td>

                                            <td className="tab-cell align-middle text-sm fw_400 text-secondary w-[15%] px-3">
                                                <div className="flex items-center">
                                                    <button
                                                        type="button"
                                                        className="cursor-pointer opacity-50"
                                                        onClick={() => {
                                                            setFieldValue("newLine", {
                                                                server_id: "",
                                                                server_name: "",
                                                                customer_selection: null,
                                                            });
                                                        }}
                                                    >
                                                        <DeleteIcon width="20" height="20" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="mt-auto flex gap-3 items-center justify-end w-full h-[4rem]">
                            <AddButton
                                onclkFn={handleSubmit}
                                prefixIcon={false}
                                text="Save"
                                type="submit"
                                className="
    flex items-center px-3 py-2  whitespace-nowrap rounded-md gap-[8px]
    bg-[var(--primary-button-bg-color)] cursor-pointer
    text-[var(--primary-button-fg-color)]
    border border-[var(--primary-button-bg-color)]
    hover:bg-[var(--primary-button-hover-color)]
    text-[length:var(--primary-button-create-fs)] 
    font-[var(--primary-button-font-weight)]
    "
                            />
                        </div>
                    </div>
                </Form>
            )}
        </Formik>
    );
}

export default ServerComp;
